import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Alert, Typography, Collapse } from 'antd';
import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import useAPIQuery from '../api/useAPIQuery';
import { t } from '../i18n';
import styles from '../pages/ApplicantAnalysis/CandidateAttachment.module.css';
import { CandidateDetail } from '../types';

export interface CandidateFileContentProps {
  candidate: CandidateDetail | undefined;
}

const ALERTS: Record<string, { message: string; type: 'warning' | 'error' }> = {
  NOT_PROCESSED: {
    message: t(
      'Aviso: O anexo enviado ainda não foi processado. Currículo não incluído nos resultados de busca.'
    ) as string,
    type: 'warning',
  },
  PROCESSING: {
    message: t(
      'Aviso: O anexo enviado esta sendo processado. Currículo não incluído nos resultados de busca.'
    ) as string,
    type: 'warning',
  },
  FAILED: {
    message: t(
      'Aviso: Não foi possivel processar o anexo enviado. Currículo não incluído nos resultados de busca.'
    ) as string,
    type: 'error',
  },
};

// Utility function to preserve line breaks in text content
const preserveLineBreaks = (text: string | null | undefined): JSX.Element | string => {
  if (!text) return text || '';
  return <span dangerouslySetInnerHTML={{ __html: String(text).replaceAll('\n', '<br>') }} />;
};

const CandidateFileContent = ({ candidate }: CandidateFileContentProps) => {
  const config = useAPIQuery('/configs/tenant-configurations/');
  const enableFileProcessingWithSearchEngine = config.data?.enableFileProcessingWithSearchEngine;
  const attachmentContent = candidate?.attachment_content ?? '';
  const candidateHasSearchIndex = candidate?.has_search_index ?? false;
  const attachmentContentStatus = candidate?.attachment_processing_status;
  const user = useAPIQuery('/users/me/');

  const [searchParams] = useSearchParams();

  // Memoize search terms extraction
  const searchTerms = useMemo(() => {
    return searchParams
      .getAll('attachment_search')
      .flatMap((value) => value.split(','))
      .map((term) => term.trim())
      .filter(Boolean);
  }, [searchParams]);

  const regex = useMemo(() => {
    return new RegExp(
      `(${searchTerms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
      'gi'
    );
  }, [searchTerms]);

  const highlightedContent = useMemo(() => {
    if (attachmentContentStatus !== 'PROCESSED' || !attachmentContent || !searchTerms.length) {
      return preserveLineBreaks(attachmentContent);
    }

    return String(attachmentContent)
      .split(regex)
      .map((part, idx) =>
        regex.test(part) ? (
          <Typography.Text mark key={idx}>
            {part}
          </Typography.Text>
        ) : (
          <span key={idx}>{preserveLineBreaks(part)}</span>
        )
      );
  }, [attachmentContentStatus, attachmentContent, searchTerms.length, regex]);

  if (
    !enableFileProcessingWithSearchEngine ||
    !candidate ||
    candidate.antivirus_file_status !== 'NO_VIRUS'
  )
    return null;

  if (attachmentContentStatus !== 'PROCESSED') {
    const alert = ALERTS[attachmentContentStatus || 'NOT_PROCESSED'];
    return (
      <Alert
        message={alert.message}
        type={alert.type}
        showIcon
        style={{ marginBottom: 8, marginTop: 8 }}
      />
    );
  }

  // Only show content if processed and content exists
  if (
    config.data?.showAttachmentContent &&
    user.data?.is_staff &&
    attachmentContentStatus === 'PROCESSED' &&
    !!attachmentContent
  ) {
    return (
      <Collapse
        ghost
        className={styles.collapse}
        expandIconPosition="end"
        defaultActiveKey={['resume_content']}
      >
        <Collapse.Panel
          header={
            <div>
              <Typography.Text strong>
                <Trans>Conteúdo do currículo</Trans>
              </Typography.Text>

              <br />
              <Typography.Text italic>
                {candidateHasSearchIndex ? (
                  <span>
                    <CheckOutlined />
                    <Trans>Candidato disponível para busca!</Trans>
                  </span>
                ) : (
                  <span style={{ color: 'red' }}>
                    <CloseOutlined />
                    <Trans>Candidato indisponível para busca!</Trans>
                  </span>
                )}
              </Typography.Text>
            </div>
          }
          key="resume_content"
        >
          <Typography.Paragraph className={styles.resumeContent}>
            {highlightedContent}
          </Typography.Paragraph>
        </Collapse.Panel>
      </Collapse>
    );
  }

  return <></>;
};

export default CandidateFileContent;
